import { Box, Chip, TextField, Typography } from '@mui/material'
import { useEffect, useState, type ClipboardEvent, type KeyboardEvent } from 'react'
import { isValidEmail } from '../../../../utils/email'

interface Props {
  emails: string[]
  onChange: (emails: string[]) => void
  onInvalidChange?: (hasInvalid: boolean) => void
}

interface InvalidEntry {
  value: string
  reason: string
}

export const EmailChipField = ({ emails, onChange, onInvalidChange }: Props): JSX.Element => {
  const [input, setInput] = useState('')
  const [invalid, setInvalid] = useState<InvalidEntry[]>([])

  useEffect(() => { onInvalidChange?.(invalid.length > 0) }, [invalid, onInvalidChange])

  const tokenize = (text: string): string[] =>
    text.split(/[,;\n\r\t]+/).map(t => t.trim()).filter(t => t.length > 0)

  const commit = (tokens: string[]): void => {
    const nextValid = [...emails]
    const nextInvalid: InvalidEntry[] = [...invalid]
    const seen = new Set(emails.map(e => e.toLowerCase()))

    tokens.forEach(token => {
      if (!isValidEmail(token)) {
        if (!nextInvalid.some(i => i.value === token)) nextInvalid.push({ value: token, reason: 'Not a valid email address' })
        return
      }
      const lower = token.toLowerCase()
      if (!seen.has(lower)) {
        seen.add(lower)
        nextValid.push(token)
      }
    })

    onChange(nextValid)
    setInvalid(nextInvalid)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
      event.preventDefault()
      if (input.trim().length > 0) {
        commit(tokenize(input))
        setInput('')
      }
    } else if (event.key === 'Backspace' && input.length === 0 && emails.length > 0) {
      onChange(emails.slice(0, -1))
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>): void => {
    const pasted = event.clipboardData.getData('text')
    if (/[,;\n\r\t]/.test(pasted)) {
      event.preventDefault()
      commit(tokenize(pasted))
      setInput('')
    }
  }

  const removeEmail = (email: string): void => { onChange(emails.filter(e => e !== email)) }
  const removeInvalid = (value: string): void => { setInvalid(invalid.filter(i => i.value !== value)) }

  return (
    <Box data-testid='p2pEmailChipField'>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mb: '8px' }}>
        {emails.map(email => (
          <Chip key={email} label={email} onDelete={() => { removeEmail(email) }} data-testid={`p2pEmailChip-${email}`} />
        ))}
        {invalid.map(entry => (
          <Chip
            key={entry.value}
            label={entry.value}
            color='error'
            variant='outlined'
            onDelete={() => { removeInvalid(entry.value) }}
            data-testid={`p2pInvalidChip-${entry.value}`}
          />
        ))}
      </Box>
      <TextField
        fullWidth
        size='small'
        placeholder='Enter email addresses (comma, newline, or paste a list)'
        value={input}
        onChange={(e) => { setInput(e.target.value) }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={() => { if (input.trim().length > 0) { commit(tokenize(input)); setInput('') } }}
        inputProps={{ 'data-testid': 'p2pEmailChipInput' }}
        helperText="Invitations can't be sent to distribution lists — enter individual email addresses."
      />
      {invalid.length > 0 &&
        <Typography variant='body2' color='error' sx={{ mt: '4px' }} data-testid='p2pInvalidSummary'>
          {invalid.length} address{invalid.length === 1 ? '' : 'es'} could not be added. Fix or remove the highlighted entries.
        </Typography>}
    </Box>
  )
}
